import { useUserContext } from 'src/context/user';
import { useQuery } from '@tanstack/react-query';
import { PremiumConfig } from 'src/api/internal/schema';
import { InternalApi, IPLocationApi } from 'src/api';
import { IPLocationResponse } from 'src/api/ipLocation/schema';
import { useUserCohorts } from 'src/hooks/useUserCohorts';
import { DiscountPriority } from 'src/utils/payment/promotionUtils';
import { useMemo } from 'react';

const DEFAULT_CURRENCY_COUNTRY = 'US';

export type PremiumConfigProps = {
  countryOverride?: string;
  specialPrice?: string;
  specialOffer?: {
    promoCode: string;
    priority?: DiscountPriority;
    isRequired?: boolean;
  } | null;
};

export function usePremiumConfig({
  countryOverride,
  specialPrice,
  specialOffer,
}: PremiumConfigProps) {
  const { loading: isLoadingUser } = useUserContext();
  const userCohorts = useUserCohorts();

  const { data: ipLocation, isLoading: isLoadingIpLocation } = useQuery<IPLocationResponse>({
    queryKey: ['getIpLocation'],
    queryFn: () => IPLocationApi.ipLocation(),
    enabled: !countryOverride,
  });

  const pageCountry = countryOverride || ipLocation?.country || DEFAULT_CURRENCY_COUNTRY;

  const {
    data: premiumConfig,
    isLoading: isLoadingConfig,
    isError,
    error,
  } = useQuery<PremiumConfig>({
    queryKey: [
      'getPremiumConfig',
      pageCountry,
      userCohorts,
      specialPrice,
      specialOffer?.promoCode,
      specialOffer?.priority,
    ],
    queryFn: () =>
      InternalApi.getPremiumConfig({
        country: pageCountry,
        userCohorts,
        specialPrice,
        specialOffer: specialOffer?.promoCode,
        specialOfferPriority: specialOffer?.priority,
      }),
    enabled: !isLoadingUser && !isLoadingIpLocation,
  });

  const requiredOfferNotPresent = useMemo(
    () =>
      specialOffer?.isRequired &&
      premiumConfig &&
      premiumConfig.prices.every(p => p.discount?.userFacingKey !== specialOffer.promoCode),
    [specialOffer, premiumConfig],
  );

  if (requiredOfferNotPresent) {
    return {
      isLoaded: false,
      isLoading: false,
      premiumConfig: undefined,
      isError: true,
      error: new Error('Required promotion code is not present'),
    };
  }

  const isLoaded = premiumConfig !== undefined;
  const isLoading = !isLoaded && (isLoadingUser || isLoadingConfig);

  return { isLoading, isLoaded, premiumConfig, isError, error };
}
